import { joiResolver } from '@hookform/resolvers/joi';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { stringToPayloadFormat } from 'web-library';
import { GenericErrorResponse } from '../../../api';
import {
  AccountActivationRequest,
  AccountActivationResponse,
  usePostAccountActivation,
} from '../../../api-refactored/user/usePostAccountActivation';
import { UseCustomMutationReturn } from '../../../api-refactored/utils/useCustomMutation';
import { AccountActivationData } from '../../../models';
import schemaAccountActivation from '../schema/schemaAccountActivation';

export interface useAccountDetail {
  /**
   * Function to trigger api to create candidate
   */
  accountDetails: (formData: AccountActivationData) => void;
  /**
   * React hook form object for the create candidate form
   */
  form: UseFormReturn<AccountActivationData>;
  /**
   * Extra handling error message for the create candidate api
   */
  submitError: string;

  /**
   * React query mutation object
   */
  accountActivationMutation: UseCustomMutationReturn<
    any,
    AxiosError<GenericErrorResponse>,
    AccountActivationRequest,
    unknown
  >;
  /**
   * show captcha flag
   */
  showCaptcha: boolean;
}

/**
 * A hook to handle candidate creation logic
 *
 * @param next Function to the next step in block flow
 */
export const useAccountDetailForm = (
  next: (data: AccountActivationResponse) => void
): useAccountDetail => {
  const [submitError, setSubmitError] = useState('');
  const [showCaptcha, setShowCaptcha] = useState(false);
  const { t } = useTranslation();

  const form = useForm<AccountActivationData>({
    mode: 'onSubmit',
    resolver: joiResolver(schemaAccountActivation),
  });

  const badRequest = 'Bad Request';

  const accountActivationMutation = usePostAccountActivation({
    onSuccess: (data) => {
      next(data);
      setShowCaptcha(false);
    },
    onError: (err) => accountActivationFailCallback(err),
  });

  const accountActivationFailCallback = (err: AxiosError<GenericErrorResponse>) => {
    const applicationCode: string = err.response?.data.code!;
    switch (err.response?.status) {
      case undefined:
        setSubmitError('');
        break;
      case 400:
        if (applicationCode === '1003') {
          setSubmitError('Date of Birth cannot be a future date.');
        } else if (applicationCode === '1002' || applicationCode === '1022') {
          setSubmitError(err.response?.data.message!);
        } else {
          setSubmitError(badRequest);
        }
        break;
      case 410:
        if (applicationCode === '1201') {
          setSubmitError(t('profile->>AccountActivation.The user could not be found.'));
        } else {
          setSubmitError(badRequest);
        }
        break;
      case 403:
        if (applicationCode === '1203') {
          setSubmitError('You have already activated your account.');
        } else if (applicationCode === '1007') {
          setError(err);
        } else if (applicationCode === '1008') {
          setSubmitError(t('profile->>AccountActivation.BlockErrorMsg'));
        } else if (applicationCode === '1009') {
          setSubmitError(t('profile->>AccountActivation.The user is locked.'));
        } else {
          setSubmitError(badRequest);
        }
        break;
      default:
        setSubmitError('Unknown error. Please try again.');
        break;
    }
    checkFailedAttempt(err);
  };

  const setError = (err: AxiosError<GenericErrorResponse>) => {
    const data = err.response?.data?.data;
    if (data) {
      const { isLocked, remainingAttempt, maxAttempt } = data;
      if (isLocked) {
        setSubmitError(t('profile->>AccountActivation.The user is locked.'));
      } else {
        setSubmitError(
          t(
            'profile->>AccountActivation.Attempts remaining: {{remainingAttempt}}. Locked after {{maxAttempt}} attempts.',
            {
              remainingAttempt,
              maxAttempt,
            }
          )
        );
      }
    } else {
      setSubmitError(t('profile->>AccountActivation.Unknown error.'));
    }
  };

  const checkFailedAttempt = (err: AxiosError<GenericErrorResponse>) => {
    setShowCaptcha(false);
    if (err.response?.data?.captchaData?.isLocked) {
      setShowCaptcha(true);
    }
  };

  const accountDetails = (formData: AccountActivationData) => {
    const data: AccountActivationData = {
      username: formData.username,
      dateOfBirth: stringToPayloadFormat(formData.dateOfBirth),
    };
    accountActivationMutation.mutate(data);
  };

  return {
    form,
    accountDetails,
    submitError,
    accountActivationMutation,
    showCaptcha,
  };
};
